'use client';

import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { reportEvent } from '@/app/utils';
import type { YoloMessageOptions } from '../../../types';

export const YOLO_MODEL_OPTIONS = [
  { value: 'auto', label: 'claw.chat.yoloModelAuto', modelVersion: 'auto', resolution: '720p', tips: '' },
  {
    value: 'q3',
    label: 'claw.chat.yoloModelQ3',
    modelVersion: '3.2',
    resolution: '1080p',
    tips: 'claw.chat.yoloModelQ3Tips',
  },
  {
    value: 'q2',
    label: 'claw.chat.yoloModelQ2',
    modelVersion: '3.1',
    resolution: '1080p',
    tips: 'claw.chat.yoloModelQ2Tips',
  },
] as const;

export type YoloModelChoice = (typeof YOLO_MODEL_OPTIONS)[number]['value'];

export const getYoloMessageOptions = (modelChoice: YoloModelChoice): YoloMessageOptions => {
  const option = YOLO_MODEL_OPTIONS.find((item) => item.value === modelChoice) ?? YOLO_MODEL_OPTIONS[0];
  return {
    modelChoice: option.value,
    modelVersion: option.modelVersion,
    resolution: option.resolution,
  };
};

interface YoloModelDropdownProps {
  value: YoloModelChoice;
  onChange: (next: YoloModelChoice) => void;
}

export const YoloModelDropdown = ({ value, onChange }: YoloModelDropdownProps) => {
  const t = useTranslations();
  const currentOption = YOLO_MODEL_OPTIONS.find((item) => item.value === value) ?? YOLO_MODEL_OPTIONS[0];

  return (
    <div className="flex h-8 shrink-0 items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild={true}>
          <button
            type="button"
            className="flex h-8 items-center gap-1.5 rounded-12 bg-system-white08 light:bg-white px-3 font-medium light:text-[rgba(18,24,38,0.74)] text-system-white80 text-xs transition-colors hover:bg-system-white12 light:hover:bg-[rgba(18,24,38,0.04)]"
          >
            <span>{t(currentOption.label)}</span>
            <ChevronDown className="size-4 light:text-[rgba(18,24,38,0.4)] text-system-white40" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[150px] light:bg-white p-1.5 light:text-[#121826]">
          <DropdownMenuLabel className="px-2 pt-1 pb-2 font-medium light:text-[rgba(18,24,38,0.56)] text-[14px] text-system-white56 leading-5">
            {t('claw.chat.yoloModelPreference')}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={value}
            onValueChange={(nextValue) => {
              const next = nextValue as YoloModelChoice;
              onChange(next);
              reportEvent('claw_element_click', { element_name: 'yolo_model_select', value: next });
            }}
          >
            {YOLO_MODEL_OPTIONS.map((option) => (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value}
                className="my-0.5 flex flex-1 flex-col items-start justify-center gap-0.5 py-1.5 text-sm light:focus:bg-[rgba(18,24,38,0.06)] light:aria-checked:bg-[rgba(18,24,38,0.06)]"
              >
                <span>{t(option.label)}</span>
                {option.tips && (
                  <span className="light:text-[rgba(18,24,38,0.48)] text-system-white48 text-xs">{t(option.tips)}</span>
                )}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
